import { commitLocalUpdate } from 'relay-runtime'

import { graphql, useQuery } from 'relay-hooks'
import { useCallback, useMemo } from 'react'
import { useRelayEnvironment } from 'react-relay'

import userModel from 'shared/utils/user'

import type { RoleType } from 'graphql/enums'

import { getUUID } from 'consumer-mobile-web/utils/jobutils'
import type { useCurrentUserQuery } from './__generated__/useCurrentUserQuery.graphql'
import type { useCurrentUserIsLoggedInQuery } from './__generated__/useCurrentUserIsLoggedInQuery.graphql'

export type Role = keyof typeof RoleType
export type User = Omit<useCurrentUserQuery['response']['viewer'], 'roles'> & {
  roles: Array<Role>
}

// TODO: remove when usages are refactored to React hooks
// eslint-disable-next-line import/no-mutable-exports -- temp workaround for rollbar
export let globalUserSSID: string | undefined

let initializing = false

const useCurrentUser = () => {
  const environment = useRelayEnvironment()
  const mobileAuth = useMemo(() => getUUID(), [])

  const setIsLoggedIn = useCallback(
    (isLoggedIn: boolean) => {
      commitLocalUpdate(environment, (store) => {
        store.getRoot().setValue(isLoggedIn, 'isLoggedIn')
      })
    },
    [environment]
  )

  if (!initializing) {
    initializing = true
    const isAuthenticated = !mobileAuth && userModel.isAuthenticated()
    setIsLoggedIn(isAuthenticated)
  }

  const { data: isLoggedInData } = useQuery<useCurrentUserIsLoggedInQuery>(
    graphql`
      query useCurrentUserIsLoggedInQuery {
        ... on Query {
          __typename
          isLoggedIn
        }
      }
    `,
    {},
    { fetchPolicy: 'store-only' }
  )

  const isLoggedIn = isLoggedInData?.isLoggedIn ?? false

  const { data, isLoading } = useQuery<useCurrentUserQuery>(
    graphql`
      query useCurrentUserQuery {
        viewer {
          ... on User {
            id
            swcid
            accessibleCompanies {
              id
              name
            }
            company {
              id
              clientCode
              swcid
              distanceUnit
              resourcesUrl
              inHouse
              language
              name
              type
              ...useCompanyInfo_company
              ...useClientAttributesFragment
            }
            email
            ctiStatus {
              id
              extension
              status
            }
            language
            name
            firstName
            permissions {
              isRoot
              isDriver
              isAdmin
              isDispatcher
              isAnsweringService
              isPartner
              isClient
              isClientAdjuster
              isClientReadOnly
              isSuperCompany
              isSwoopDispatcher
              isCtiEnabled
              isInsightsEnabled
              isCommunicationsIntegrationEnabled
            }
            roles {
              nodes {
                id
                name
              }
            }
            timezone
            userName
          }
        }
      }
    `,
    {},
    {
      skip: !isLoggedIn,
      onResponse(response) {
        // @ts-expect-error -- temp workaround for rollbar
        const user = (response?.data as useCurrentUserQuery['response'])?.viewer
        if (!user) {
          return
        }

        if (!globalUserSSID) {
          globalUserSSID = user.id
        }
      },
    }
  )

  const endpoint = useMemo(() => {
    if (data?.viewer?.permissions?.isSuperCompany) {
      return 'root'
    }
    if (data?.viewer?.permissions?.isClient) {
      return 'fleet'
    }
    return 'partner'
  }, [data?.viewer?.permissions])

  const permissions = useMemo(() => {
    if (!data?.viewer.permissions) {
      return {} as NonNullable<
        useCurrentUserQuery['response']['viewer']['permissions'] & {
          isOnlyDriver: boolean
          isOnlyClientAdjuster: boolean
          isOnlyClientReadOnly: boolean
          canMutateJobs: boolean
        }
      >
    }

    const p = data.viewer.permissions
    const isOnlyClientAdjuster = p.isClientAdjuster && !p.isDriver && !p.isAdmin && !p.isDispatcher
    const isOnlyClientReadOnly = p.isClientReadOnly && !p.isDriver && !p.isAdmin && !p.isDispatcher

    return {
      isOnlyClientAdjuster,
      isOnlyClientReadOnly,
      canMutateJobs: !isOnlyClientAdjuster && !isOnlyClientReadOnly,
      isOnlyDriver: p.isDriver && !p.isAdmin && !p.isDispatcher,
      ...p,
    }
  }, [data?.viewer.permissions])

  const currentUser = useMemo(
    () => ({
      ...data?.viewer,
      permissions,
      company:
        data?.viewer?.company ??
        ({} as NonNullable<useCurrentUserQuery['response']['viewer']['company']>),
      accessibleCompanies: [...(data?.viewer?.accessibleCompanies ?? [])],
      roles: data?.viewer.roles?.nodes.map((node) => node.name as Role) ?? [],
    }),
    [data?.viewer, permissions]
  )

  return {
    currentUser,
    isLoading,
    isLoggedIn,
    setIsLoggedIn,
    endpoint,
  }
}

export default useCurrentUser
